import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { User, MapPin, Award, Key, ChevronRight, Plus, Trash2, Edit2 } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { userService } from '@/services/user.service';
import { Address, LoyaltyTransaction } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/common/Skeleton';
import { formatCurrency, formatDate } from '@/utils';
import toast from '@/hooks/useToast';

export const ProfilePage = () => {
  const location = useLocation();
  const navItems = [
    { path: '/profile', label: 'My Profile', icon: User, exact: true },
    { path: '/profile/addresses', label: 'Addresses', icon: MapPin },
    { path: '/profile/loyalty', label: 'Milan Points', icon: Award },
    { path: '/profile/security', label: 'Security', icon: Key },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="font-heading font-bold text-2xl text-gray-800 mb-6">My Account</h1>
      <div className="grid md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <aside className="md:col-span-1">
          <nav className="bg-white rounded-card shadow-card overflow-hidden">
            {navItems.map(item => {
              const active = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-l-2 ${active ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-transparent text-gray-600 hover:bg-gray-50'}`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  <ChevronRight className="h-3 w-3 ml-auto opacity-40" />
                </Link>
              );
            })}
          </nav>
        </aside>
        <div className="md:col-span-3">
          <Routes>
            <Route index element={<ProfileInfo />} />
            <Route path="addresses" element={<AddressesPanel />} />
            <Route path="loyalty" element={<LoyaltyPanel />} />
            <Route path="security" element={<SecurityPanel />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const ProfileInfo = () => {
  const { user, updateUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await userService.updateProfile(form);
      updateUser(res.data.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Could not update profile'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-semibold text-gray-800">Personal Information</h2>
        {!editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Edit2 className="h-4 w-4 mr-2" />Edit</Button>}
      </div>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-2xl">
          {user?.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-heading font-semibold text-xl text-gray-800">{user?.name}</p>
          <p className="text-sm text-gray-400">{user?.email}</p>
          {user?.role !== 'CUSTOMER' && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full mt-1 inline-block capitalize">{user?.role.toLowerCase()}</span>
          )}
        </div>
      </div>
      {editing ? (
        <div className="space-y-4">
          <Input label="Full Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          <Input label="Phone Number" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91XXXXXXXXXX" />
          <div className="flex gap-3">
            <Button loading={saving} onClick={save}>Save Changes</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <InfoField label="Full Name" value={user?.name} />
          <InfoField label="Email" value={user?.email} />
          <InfoField label="Phone" value={user?.phone || '—'} />
          <InfoField label="Member Since" value={user?.createdAt ? formatDate(user.createdAt) : '—'} />
          <InfoField label="Account Status" value={user?.isVerified ? '✓ Verified' : 'Not verified'} />
          <InfoField label="Milan Points" value={`${user?.loyaltyPoints || 0} pts`} />
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value }: { label: string; value?: string }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className="font-medium text-gray-800">{value || '—'}</p>
  </div>
);

const AddressesPanel = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: 'Home', line1: '', line2: '', city: 'Nadiad', state: 'Gujarat', pincode: '', isDefault: false });

  useEffect(() => {
    userService.getAddresses().then(res => setAddresses(res.data.data)).finally(() => setLoading(false));
  }, []);

  const addAddress = async () => {
    try {
      const res = await userService.addAddress(form as Omit<Address, 'id' | 'userId'>);
      setAddresses(prev => [...prev, res.data.data]);
      setShowForm(false);
      toast.success('Address added!');
    } catch { toast.error('Could not add address'); }
  };

  const deleteAddress = async (id: string) => {
    try {
      await userService.deleteAddress(id);
      setAddresses(prev => prev.filter(a => a.id !== id));
      toast.success('Address deleted');
    } catch { toast.error('Could not delete address'); }
  };

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-semibold text-gray-800">Saved Addresses</h2>
        <Button size="sm" onClick={() => setShowForm(true)}><Plus className="h-4 w-4 mr-2" />Add New</Button>
      </div>
      {loading ? <Skeleton className="h-32" /> : (
        <div className="space-y-3">
          {addresses.map(addr => (
            <div key={addr.id} className={`border-2 rounded-xl p-4 ${addr.isDefault ? 'border-primary-300 bg-primary-50' : 'border-gray-100'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-800 text-sm">{addr.label}</span>
                    {addr.isDefault && <span className="text-xs bg-primary-200 text-primary-800 px-2 py-0.5 rounded-full">Default</span>}
                  </div>
                  <p className="text-sm text-gray-600">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}</p>
                  <p className="text-sm text-gray-500">{addr.city}, {addr.state} — {addr.pincode}</p>
                </div>
                <button onClick={() => deleteAddress(addr.id)} className="text-gray-400 hover:text-error p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {addresses.length === 0 && <p className="text-gray-400 text-sm">No addresses saved yet.</p>}
        </div>
      )}
      {showForm && (
        <div className="mt-6 border-2 border-dashed border-primary-200 rounded-xl p-5 space-y-3">
          <h3 className="font-semibold text-gray-800">New Address</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {['Home', 'Work', 'Other'].map(l => (
              <button key={l} onClick={() => setForm(p => ({ ...p, label: l }))} className={`px-3 py-2 rounded-xl text-sm border-2 ${form.label === l ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'}`}>{l}</button>
            ))}
          </div>
          <Input placeholder="Address Line 1 *" value={form.line1} onChange={e => setForm(p => ({ ...p, line1: e.target.value }))} />
          <Input placeholder="Landmark / Area" value={form.line2} onChange={e => setForm(p => ({ ...p, line2: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="City" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} />
            <Input placeholder="Pincode" value={form.pincode} maxLength={6} onChange={e => setForm(p => ({ ...p, pincode: e.target.value }))} />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.isDefault} onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))} className="accent-primary-500" />
            Set as default address
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={addAddress}>Save</Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const LoyaltyPanel = () => {
  const { user } = useAuthStore();
  const [data, setData] = useState<{ balance: number; transactions: LoyaltyTransaction[] } | null>(null);

  useEffect(() => {
    userService.getLoyalty().then(res => setData(res.data.data));
  }, []);

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <h2 className="font-heading font-semibold text-gray-800 mb-4">Milan Points</h2>
      <div className="bg-gradient-to-r from-accent to-primary-500 rounded-2xl p-6 text-white mb-6">
        <p className="text-sm font-medium text-white/80 mb-1">Current Balance</p>
        <p className="font-heading font-bold text-4xl">{data?.balance ?? user?.loyaltyPoints ?? 0}</p>
        <p className="text-sm text-white/80 mt-1">points = {formatCurrency(Math.floor((data?.balance || 0) / 100) * 10)} redeemable</p>
        <div className="mt-4 text-xs text-white/70">100 points = ₹10 off • Earn 1 point per ₹10 spent</div>
      </div>
      <h3 className="font-heading font-semibold text-gray-700 mb-3">Transaction History</h3>
      {data?.transactions.length === 0 && <p className="text-gray-400 text-sm">No transactions yet.</p>}
      <div className="space-y-2">
        {data?.transactions.map(t => (
          <div key={t.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
            <div>
              <p className="text-sm font-medium text-gray-800">{t.description || (t.type === 'EARNED' ? 'Points Earned' : 'Points Redeemed')}</p>
              <p className="text-xs text-gray-400">{formatDate(t.createdAt)}</p>
            </div>
            <span className={`font-bold ${t.type === 'EARNED' ? 'text-success' : 'text-error'}`}>
              {t.type === 'EARNED' ? '+' : ''}{t.points} pts
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SecurityPanel = () => {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.newPassword !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await userService.changePassword(form.currentPassword, form.newPassword);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully!');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not change password';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <h2 className="font-heading font-semibold text-gray-800 mb-6">Change Password</h2>
      <div className="max-w-sm space-y-4">
        <Input label="Current Password" type="password" value={form.currentPassword} onChange={e => setForm(p => ({ ...p, currentPassword: e.target.value }))} />
        <Input label="New Password" type="password" value={form.newPassword} onChange={e => setForm(p => ({ ...p, newPassword: e.target.value }))} />
        <Input label="Confirm New Password" type="password" value={form.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} />
        <Button loading={saving} onClick={save}>Update Password</Button>
      </div>
    </div>
  );
};
